import { COMMAND_CENTER_ROOT, commandCenterPath } from './commandCenterRoutes'
import { useParams, useNavigate } from 'react-router-dom'
import { OperationsMiniPortal, getOperationsModule } from './operations'
import { useOperationsLiveQueue } from './operations/liveQueues'
import { ClientIntakeWorkroom } from './operations/client-intake'

/**
 * V2 Operations mini portal — route wrapper.
 *
 * Registered standalone + auth-gated (see router.tsx):
 *   /square/command-center/operations/:moduleId
 *
 * Most modules render the generic workable mini portal (GenericModulePortal
 * below, which owns the live-queue hook). A few modules have a dedicated,
 * perfected workroom — a faithful port of the operator's design — and render
 * that instead; currently Client Intake. Unknown ids fall through to the
 * generic portal's honest "module not found" state (no dead end). Splitting
 * the two render paths into separate components keeps every hook unconditional.
 */
export default function SquareCodeOperationsModuleRoute() {
  const { moduleId } = useParams<{ moduleId: string }>()
  const navigate = useNavigate()

  const toCommandCenter = () => navigate(COMMAND_CENTER_ROOT)
  const toOperations = () => navigate(commandCenterPath('operations'))

  if (moduleId === 'client-intake') {
    return (
      <ClientIntakeWorkroom
        onHome={toCommandCenter}
        onBackCommandCenter={toCommandCenter}
        onBackOperations={toOperations}
      />
    )
  }

  return <GenericModulePortal moduleId={moduleId} onNavigate={(to) => navigate(to)} onCommandCenter={toCommandCenter} onOperations={toOperations} />
}

function GenericModulePortal({
  moduleId, onNavigate, onCommandCenter, onOperations,
}: {
  moduleId: string | undefined
  onNavigate: (to: string) => void
  onCommandCenter: () => void
  onOperations: () => void
}) {
  const module = getOperationsModule(moduleId)
  const liveQueue = useOperationsLiveQueue(moduleId)
  return (
    <OperationsMiniPortal
      module={module}
      liveQueue={liveQueue}
      onOpenRoute={onNavigate}
      onBackCommandCenter={onCommandCenter}
      onBackOperations={onOperations}
      onHome={onCommandCenter}
    />
  )
}
